import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '../services/api'
import { MarketplaceListing } from '../types'
import { TrendingUp, Clock, DollarSign, ExternalLink, LogIn } from 'lucide-react'
import BuyNFTModal from '../components/marketplace/BuyNFTModal'
import AuthModal from '../components/auth/AuthModal'
import { getTierDisplayName } from '../utils/vault.utils'
import { useConfig } from '../contexts/ConfigContext'
import { BLOCKCHAIN_CONFIG } from '../config/app.config'

export default function MarketplacePage() {
  const { t } = useTranslation(['marketplace', 'common'])
  const queryClient = useQueryClient()
  const config = useConfig()

  // Get current user to verify token is valid
  const { data: currentUserResponse } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => api.getCurrentUser(),
    enabled: api.isAuthenticated(),
    retry: false,
  })
  const currentUser = currentUserResponse?.data

  // Check BOTH token exists AND user data loaded (token is valid)
  const isAuthenticated = api.isAuthenticated() && !!currentUser
  
  const [sortBy, setSortBy] = useState<string>('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [tierFilter, setTierFilter] = useState<string>('ALL')
  const [selectedListing, setSelectedListing] = useState<MarketplaceListing | null>(null)
  const [isBuyModalOpen, setIsBuyModalOpen] = useState(false)
  const [authModalOpen, setAuthModalOpen] = useState(false)

  const { data: listingsResponse, isLoading } = useQuery({
    queryKey: ['marketplace', sortBy, sortOrder, tierFilter],
    queryFn: () =>
      api.getMarketplaceListings({
        status: 'ACTIVE',
        sortBy,
        sortOrder,
      }),
  })

  const { data: statsResponse } = useQuery({
    queryKey: ['marketplaceStats'],
    queryFn: () => api.getMarketplaceStats(),
  })

  const listings = listingsResponse?.data || []
  const stats = statsResponse?.data

  const filteredListings =
    tierFilter === 'ALL'
      ? listings
      : listings.filter((listing) => listing.vault.tierName === tierFilter)

  const handleBuyClick = (listing: MarketplaceListing) => {
    setSelectedListing(listing)
    setIsBuyModalOpen(true)
  }

  // Check if marketplace is disabled
  if (config.marketplace.disableMarketplaceSale) {
    return (
      <div className="min-h-screen py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center space-y-4 mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-white">{t('marketplace:page.title')}</h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              {t('marketplace:page.subtitle')}
            </p>
          </div>

          {/* Coming Soon Message */}
          <div className="text-center py-20">
            <div className="bg-background-card rounded-xl p-12 border border-gold-500/30 max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gold-500 mb-4">{t('marketplace:page.comingSoonTitle')}</h2>
              <p className="text-lg text-gray-300 mb-6">
                {t('marketplace:page.comingSoonMessage')}
              </p>
              <p className="text-gray-400">
                {t('marketplace:page.comingSoonDescription')}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-white">{t('marketplace:page.title')}</h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            {t('marketplace:page.subtitle')}
          </p>
        </div>

        {/* Marketplace Stats */}
        {stats && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-background-card rounded-xl p-6 border border-green-900/20">
              <div className="text-sm text-gray-400 mb-2">{t('marketplace:stats.totalListings')}</div>
              <div className="text-2xl font-bold text-white">{stats.totalListings || 0}</div>
            </div>
            <div className="bg-background-card rounded-xl p-6 border border-green-900/20">
              <div className="text-sm text-gray-400 mb-2">{t('marketplace:stats.totalVolume')}</div>
              <div className="text-2xl font-bold text-gold-500">
                ${(stats.totalVolume || 0).toLocaleString()}
              </div>
            </div>
            <div className="bg-background-card rounded-xl p-6 border border-green-900/20">
              <div className="text-sm text-gray-400 mb-2">{t('marketplace:stats.floorPrice')}</div>
              <div className="text-2xl font-bold text-white">
                ${(stats.floorPrice || 0).toLocaleString()}
              </div>
            </div>
            <div className="bg-background-card rounded-xl p-6 border border-green-900/20">
              <div className="text-sm text-gray-400 mb-2">{t('marketplace:stats.activeListings')}</div>
              <div className="text-2xl font-bold text-green-400">
                {stats.activeListings || 0}
              </div>
            </div>
          </div>
        )}

        {/* Filters & Sort */}
        <div className="bg-background-card rounded-xl p-6 mb-8 border border-green-900/20">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Tier Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                {t('marketplace:filters.title')}
              </label>
              <div className="flex flex-wrap gap-2">
                {['ALL', ...new Set(listings.map(l => l.vault.tierName).filter(Boolean))].map((tier) => (
                  <button
                    key={tier}
                    onClick={() => setTierFilter(tier)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      tierFilter === tier
                        ? 'bg-gold-500 text-background-primary'
                        : 'bg-background-elevated text-gray-300 hover:bg-green-900/20'
                    }`}
                  >
                    {tier === 'ALL' ? t('marketplace:filters.allTiers') : tier}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">{t('marketplace:sort.sortBy')}</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 bg-background-elevated border border-green-900/30 rounded-lg text-white focus:outline-none focus:border-gold-500"
              >
                <option value="createdAt">{t('marketplace:sort.newest')}</option>
                <option value="priceUSDT">{t('marketplace:sort.price')}</option>
                <option value="finalAPY">{t('marketplace:sort.apy')}</option>
                <option value="remainingMonths">{t('marketplace:sort.timeRemaining')}</option>
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">{t('marketplace:sort.order')}</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                className="w-full px-4 py-2 bg-background-elevated border border-green-900/30 rounded-lg text-white focus:outline-none focus:border-gold-500"
              >
                <option value="desc">{t('marketplace:sort.descending')}</option>
                <option value="asc">{t('marketplace:sort.ascending')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Listings Grid */}
        {isLoading ? (
          <div className="text-center py-20">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-gray-400">{t('marketplace:loading')}</p>
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400">{t('marketplace:noListings')}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredListings.map((listing) => (
              <div
                key={listing.id}
                className="bg-background-card rounded-xl p-6 border border-green-900/20 card-glow hover:border-gold-500/50 transition-all"
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className={`tier-${(listing.vault.tierName || '').toLowerCase()} inline-block`}>
                    {getTierDisplayName(listing.vault.tierName || '')}
                  </div>
                  {listing.nftMintAddress && (
                    <a
                      href={`https://solscan.io/token/${listing.nftMintAddress}${BLOCKCHAIN_CONFIG.solana.network === 'devnet' ? '?cluster=devnet' : ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-gold-400 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>

                {/* Vault Name */}
                <h3 className="text-lg font-bold text-white mb-1">{listing.vault.name}</h3>
                <p className="text-sm text-gray-400 mb-4">
                  {t('marketplace:card.monthsVault', { months: listing.vault.duration })}
                </p>

                {/* Price */}
                <div className="bg-gradient-to-br from-gold-500/10 to-transparent border border-gold-500/30 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-gold-500" />
                    <span className="text-sm text-gray-400">{t('marketplace:card.listingPrice')}</span>
                  </div>
                  <div className="text-3xl font-bold text-gold-500">
                    ${listing.priceUSDT.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">
                    {t('marketplace:card.original')}: ${listing.originalInvestment.toLocaleString()}
                  </div>
                </div>

                {/* Stats */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between p-3 bg-background-elevated rounded-lg">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-gold-500" />
                      <span className="text-sm text-gray-400">{t('marketplace:card.usdtAPY')}</span>
                    </div>
                    <span className="text-sm font-semibold text-gold-500">
                      {listing.finalAPY}%
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-background-elevated rounded-lg">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-teal-400" />
                      <span className="text-sm text-gray-400">{t('marketplace:card.timeLeft')}</span>
                    </div>
                    <span className="text-sm font-semibold text-white">
                      {listing.remainingMonths} {t('marketplace:card.months')}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-background-elevated rounded-lg">
                    <span className="text-sm text-gray-400">{t('marketplace:card.earnedUSDT')}</span>
                    <span className="text-sm font-semibold text-green-400">
                      ${listing.totalEarnedUSDT.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-background-elevated rounded-lg">
                    <span className="text-sm text-gray-400">{t('marketplace:card.minedTAKARA')}</span>
                    <span className="text-sm font-semibold text-green-400">
                      {listing.totalMinedTAKARA.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* LAIKA Boost */}
                {listing.laikaBoost && (
                  <div className="bg-gradient-laika/10 border border-laika-purple/30 rounded-lg p-3 mb-4">
                    <div className="text-xs text-laika-purple font-medium mb-2">
                      {t('marketplace:card.laikaBoostActive')}
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">{t('marketplace:card.boostUSDTAPY')}</span>
                      <span className="text-laika-green font-semibold">
                        +{listing.laikaBoost.additionalAPY}%
                      </span>
                    </div>
                  </div>
                )}

                {/* Seller Info */}
                <div className="text-xs text-gray-500 mb-4">
                  {t('marketplace:card.seller')}: {listing.seller.walletAddress
                    ? `${listing.seller.walletAddress.slice(0, 6)}...${listing.seller.walletAddress.slice(-4)}`
                    : listing.seller.username || t('marketplace:card.anonymous')}
                </div>

                {/* Platform Fee */}
                <div className="text-xs text-gray-500 mb-4">
                  {t('marketplace:card.platformFee')}: {listing.platformFee}%
                </div>

                {/* Buy Button */}
                {!isAuthenticated ? (
                  <button
                    onClick={() => setAuthModalOpen(true)}
                    className="btn-gold w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                  >
                    <LogIn className="h-4 w-4" />
                    {t('marketplace:card.loginToBuy')}
                  </button>
                ) : (
                  <button
                    onClick={() => handleBuyClick(listing)}
                    className="btn-gold w-full py-3 rounded-lg font-semibold"
                  >
                    {t('marketplace:card.buyNow')}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Buy Wexel Modal */}
      {selectedListing && (
        <BuyNFTModal
          isOpen={isBuyModalOpen}
          onClose={() => {
            setIsBuyModalOpen(false)
            setSelectedListing(null)
          }}
          listing={selectedListing}
        />
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['currentUser'] })
        }}
      />
    </div>
  )
}
